// Legacy Chromium polyfills (Chrome ~90 / Samsung S8 WebView). MUST run before
// any other module touches Array.prototype.at, Object.hasOwn,
// crypto.randomUUID, or structuredClone. See
// frontend/.parity-loop/android-s8/chromium-features-audit.md.
import "./lib/polyfills/legacy-chromium";

import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

import { AppShell } from "./app";
import { RouteLoadingSpinner } from "./components/AppSpinner";
import { cleanupLegacyServiceWorker } from "./features/pwa/lib/cleanup-service-worker";
import { applyDesktopShellFromBootFetch } from "./features/desktop/desktop-shell-flag";
import { applyGamificationFlagsFromBootFetch } from "./features/gamification/gamification-flag";
import {
  parseDevSelfPreviewEnabled,
  setDevSelfPreviewEnabled,
} from "./features/gamification/dev-self-preview-flag";
import { coalesceAdminLayoutRoutes } from "./features/admin/_layout/adminRouteTopology";
import { isStaticGamificationShowcasePath } from "./features/gamification/showcase/static-showcase-path";
import {
  fetchPublicFeatureFlagsRaw,
  mergePwaFlags,
} from "./features/pwa/lib/fetch-pwa-flags";
import { setPwaFlagsCache } from "./features/pwa/lib/flags-cache";
import { isPwaFeatureOn } from "./features/pwa/lib/pwa-flags";
import { registerQuartelServiceWorker } from "./pwa/register-service-worker";
import { generatedRoutes } from "./routes.generated";
import "./styles/globals.css";

import { invariant } from "@/lib/utils/invariant";

type ScrollToArgs = [options?: ScrollToOptions] | [x: number, y: number];
const BOOT_SCROLL_Y_KEY = "quartel.bootScrollY";

const rootEl = document.getElementById("root");
invariant(rootEl, "expected #root in index.html (data mode bootstrap)");

installRequestedScrollRecorder();

const staticShowcase = isStaticGamificationShowcasePath(
  window.location.pathname,
);
if (!staticShowcase) void initializeConnectedRuntime();

// Connected boot: ONE fetch of the public feature-flags envelope feeds BOTH the
// PWA snapshot and the #216 desktop-shell kill switch (they share the endpoint).
// A single fetch removes the prior double-request that raced on the server's
// 5s Cache-Control. Errors never block render — a null map keeps the PWA flags
// off. Desktop-shell and gamification caches are exceptions: on a null map
// (fetch failure) their boot guards KEEP the last known-good snapshot and do
// not treat transport failure as an authoritative operator rollback. The
// static Pages showcase intentionally skips this entire runtime so its public
// review URL performs no API calls.
async function initializeConnectedRuntime(): Promise<void> {
  const raw = await fetchPublicFeatureFlagsRaw();
  const pwaFlags = mergePwaFlags(raw);
  setPwaFlagsCache(pwaFlags);
  applyDesktopShellFromBootFetch(raw);
  applyGamificationFlagsFromBootFetch(raw);
  setDevSelfPreviewEnabled(parseDevSelfPreviewEnabled(raw));
  if (isPwaFeatureOn(pwaFlags, "pwa.service_worker_enabled")) {
    registerQuartelServiceWorker(pwaFlags);
  } else {
    await cleanupLegacyServiceWorker();
  }
}

const router = createBrowserRouter([
  {
    Component: AppShell,
    children: coalesceAdminLayoutRoutes(generatedRoutes),
  },
]);

// Router-root Suspense fallback: surfaces the canonical `app-spinner`
// (C53-SPINNER-ANTI-FLASH-500MS) for any lazy boundary that escapes the
// AppShell's inner Suspense (e.g. a future code-split of AppShell itself).
// The AppShell's inner Suspense already wraps the lazy route Outlet with the
// same testid via RouterRootSuspenseFallback — see src/app.tsx.
ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <Suspense fallback={<RouteLoadingSpinner />}>
      <RouterProvider router={router} />
    </Suspense>
  </React.StrictMode>,
);

// DEV-only: expose `window.quartelShowResult(kind?, reais?)` to preview the
// trade result popup without a settled trade. Dynamic import keeps it out of
// the production bundle (the branch is dead and dropped at build time).
if (import.meta.env.DEV) {
  void import("./features/robot/components/flow/devResultOverlayTrigger").then(
    (m) => m.installResultOverlayDevTrigger(),
  );
}

function installRequestedScrollRecorder(): void {
  const originalScrollTo = window.scrollTo.bind(window) as (
    x?: number | ScrollToOptions,
    y?: number,
  ) => void;
  window.scrollTo = function scrollToWithRecord(...args: ScrollToArgs): void {
    const requestedY = requestedScrollY(args);
    recordRequestedScrollY(requestedY);
    if (typeof args[0] === "number") originalScrollTo(args[0], args[1] ?? 0);
    else originalScrollTo(args[0]);
  } as Window["scrollTo"];
}

function recordRequestedScrollY(requestedY: number): void {
  if (requestedY > 0) {
    window.sessionStorage.setItem(BOOT_SCROLL_Y_KEY, String(requestedY));
    return;
  }
  window.sessionStorage.removeItem(BOOT_SCROLL_Y_KEY);
}

function requestedScrollY(args: ScrollToArgs): number {
  const [first, second] = args;
  if (typeof first === "object" && first !== null)
    return Number(first.top ?? 0);
  if (typeof second === "number") return second;
  return 0;
}
